import { Component, inject, ElementRef, ViewChild, OnInit } from '@angular/core';
import { DataShareService } from '../../service/data-share.service';
import { ApiService } from '../../service/api.service';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from "@angular/material/button";
import { PowProgressComponent } from '../../helper/pow-progress/pow-progress.component';
import { PowService, PowSolution } from '../../service/pow.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-gamenight',
  imports: [
    PowProgressComponent,
    MatCardModule, 
    MatIconModule, 
    MatButtonModule, 
    MatButtonModule
  ],
  templateUrl: './gamenight.component.html',
  styleUrl: './gamenight.component.less'
})
export class GamenightComponent implements OnInit {

  dataShareService = inject(DataShareService);
  apiService = inject(ApiService);
  snackBar = inject(MatSnackBar);
  powService = inject(PowService);

  // Proof of work: the vote button stays disabled until pow is set
  pow: PowSolution | null = null;
  powError = false;
  activeIndex = 0;
  isSubmitting = false;
  @ViewChild('selectionStatus', { static: false }) selectionStatus?: ElementRef;
  ngOnInit(): void {
    this.startChallenge();
  }

  async startChallenge(): Promise<void> {
    this.pow = null;
    this.powError = false;
    try {
      this.pow = await this.powService.solve('gamenight');
    } catch (e) {
      console.error('Failed to solve vote challenge:', e);
      this.powError = true;
    }
  }

  get activeOptionId(): string | null {
    const options = this.dataShareService.gameNightData.value?.options ?? [];
    if (!options.length) return null;
    const index = Math.min(this.activeIndex, options.length - 1);
    return `option-${index}`;
  }

  onListFocus(): void {
    const options = this.dataShareService.gameNightData.value?.options ?? [];
    if (this.activeIndex >= options.length) {
      this.activeIndex = Math.max(0, options.length - 1);
    }
  }

  onListKeydown(event: KeyboardEvent): void {
    const options = this.dataShareService.gameNightData.value?.options ?? [];
    if (!options.length) return;


    let offset = 0;

    const key = event.key;
    if (key === 'ArrowRight') {
      event.preventDefault();
      offset = 1;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      offset = -1;
    }


    let columns = 4;
    if (key === 'ArrowUp') {
      event.preventDefault();
      offset = -columns;
    }
    if (key === 'ArrowDown') {
      event.preventDefault();
      offset = columns;
    }

    this.activeIndex = (this.activeIndex + offset + options.length) % options.length;

    setTimeout(() => {
      document.querySelector(`.option-card.active`)!.scrollIntoView({ behavior: 'smooth' });
    })

    if (key === 'Home') {
      event.preventDefault();
      this.activeIndex = 0;
      return;
    }

    if (key === 'End') {
      event.preventDefault();
      this.activeIndex = options.length - 1;
      return;
    }

    if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      const option = options[this.activeIndex];
      if (option) {
        option.selected = !option.selected;
        this.announceSelection(option.title, option.selected);
      }
      return;
    }
  }

  toggleOption(option: any, index: number): void {
    option.selected = !option.selected;
    this.announceSelection(option.title, option.selected);
  }

  announceSelection(optionTitle: string, selected: boolean): void {
    const statusElement = document.getElementById('selection-status');
    if (statusElement) {
      const status = selected ? 'selected' : 'deselected';
      statusElement.textContent = `${optionTitle} ${status}`;
    }
  }

  submitVote(): void {
    if (this.isSubmitting) return;

    if (!this.pow) {
      if (this.powError) this.startChallenge();
      return;
    }
    
    const options = this.dataShareService.gameNightData.value?.options ?? [];
    const selectedOptions = options.filter(option => option.selected);
    
    if (selectedOptions.length === 0) {
      this.snackBar.open('Please select at least one option before voting', 'Close', {
        duration: 3000
      });
      return;
    }
    
    this.isSubmitting = true;
    // A challenge can only be redeemed once, so grab a new one whatever the outcome
    const pow = this.pow;
    this.startChallenge();

    const selectedOptionIds = selectedOptions.map(option => option.id);
    
    this.apiService.post('gamenight/submitVote.php', {
      selected_options: selectedOptionIds,
      pow
    }).subscribe({
      next: (response) => {
        this.snackBar.open('Vote submitted successfully!', 'Close', {
          duration: 3000
        });
        
        // Refresh the data to show updated vote counts
        // Wait for refresh to complete before clearing selections
        this.dataShareService.fetchGameNightData().subscribe({
          next: () => {
            // Clear selections after refresh completes
            // The server will have already updated the vote counts
            const currentOptions = this.dataShareService.gameNightData.value?.options ?? [];
            currentOptions.forEach(option => option.selected = false);
          },
          error: () => {
            // If refresh fails, still clear selections to prevent confusion
            const currentOptions = this.dataShareService.gameNightData.value?.options ?? [];
            currentOptions.forEach(option => option.selected = false);
          }
        });
      },
      error: (error) => {
        this.snackBar.open('Failed to submit vote. Please try again.', 'Close', {
          duration: 5000
        });
        console.error('Vote submission error:', error);
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }
}
